import { useState, useEffect, useCallback, useRef } from 'react';
import { AxiosResponse } from 'axios';

interface UseAnalyticsDataResult<T> {
    data: T | null;
    loading: boolean;
    error: string | null;
    errorStatus: number | null;
    refetch: () => void;
}

const useAnalyticsData = <T>(
    fetchFn: () => Promise<AxiosResponse<T>>,
    deps: unknown[] = [],
    enabled = true
): UseAnalyticsDataResult<T> => {
    const [data, setData] = useState<T | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [errorStatus, setErrorStatus] = useState<number | null>(null);
    const fetchRef = useRef(fetchFn);
    fetchRef.current = fetchFn;

    const load = useCallback(() => {
        if (!enabled) {
            setData(null);
            setLoading(false);
            setError(null);
            setErrorStatus(null);
            return () => undefined;
        }

        let cancelled = false;
        setLoading(true);
        setError(null);
        setErrorStatus(null);

        fetchRef
            .current()
            .then((res) => {
                if (!cancelled) setData(res.data);
            })
            .catch((err) => {
                if (!cancelled) {
                    setError(err?.response?.data?.message || err.message || 'Failed to load data');
                    setErrorStatus(err?.response?.status ?? null);
                }
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [...deps, enabled]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const cleanup = load();
        return cleanup;
    }, [load]);

    const refetch = useCallback(() => {
        load();
    }, [load]);

    return { data, loading, error, errorStatus, refetch };
};

export default useAnalyticsData;
